import {
  collection,
  doc,
  getDocs,
  getDoc,
  query,
  where,
  setDoc,
  addDoc,
  db
} from '../config/firebase';
import { transformDocument, camelToSnake } from './firestoreService';

const USERS_COLLECTION = 'users';

/**
 * Generate display name from full name
 * Simple case: "John Smith" → "John S"
 * With hyphen: "Jeff Tom-Smith" → "Jeff TS"
 * @param {string} name - Full name (e.g., "John Smith" or "Jeff Tom-Smith")
 * @returns {string} Display name
 */
const generateDisplayName = (name) => {
  if (!name || typeof name !== 'string') {
    return '';
  }

  const trimmedName = name.trim();
  if (!trimmedName) {
    return '';
  }

  const nameParts = trimmedName.split(/\s+/).filter(part => part.length > 0);
  if (nameParts.length === 0) {
    return '';
  }

  const firstName = nameParts[0];

  // If only one name part, return just the first name
  if (nameParts.length === 1) {
    return firstName;
  }

  // Last name is the last word (handles middle names correctly)
  const lastName = nameParts[nameParts.length - 1];

  // Check if last name contains a hyphen
  if (lastName.includes('-')) {
    // Split by hyphen and get first letter of each part
    const hyphenParts = lastName.split('-').filter(part => part.length > 0);
    const initials = hyphenParts.map(part => part.charAt(0).toUpperCase()).join('');
    return `${firstName} ${initials}`;
  } else {
    // Simple case: first letter of last name
    const lastInitial = lastName.charAt(0).toUpperCase();
    return `${firstName} ${lastInitial}`;
  }
};

/**
 * Generate a unique display name by adding progressive initials from last name
 * For hyphenated names like "Smith-Silver": starts with "SS", then "SSi", "SSil", etc.
 * For simple names like "Smith": starts with "S", then "Sm", "Smi", etc.
 * @param {string} firstName - First name
 * @param {string} lastName - Last name (may be hyphenated)
 * @param {Set<string>} existingNames - Set of existing display names (lowercase for comparison)
 * @returns {string} Unique display name
 */
const generateUniqueDisplayName = (firstName, lastName, existingNames) => {
  const isHyphenated = lastName.includes('-');
  
  if (isHyphenated) {
    // For hyphenated names like "Smith-Silver"
    const hyphenParts = lastName.split('-').filter(part => part.length > 0);
    if (hyphenParts.length >= 2) {
      const firstPart = hyphenParts[0];
      const secondPart = hyphenParts[hyphenParts.length - 1];
      const baseInitials = firstPart.charAt(0).toUpperCase() + secondPart.charAt(0).toUpperCase();
      
      // First try: "John SS"
      let candidateName = `${firstName} ${baseInitials}`;
      if (!existingNames.has(candidateName.toLowerCase())) {
        return candidateName;
      }
      
      // Then try: "John SSi", "John SSil", "John SSilv", etc.
      for (let i = 2; i <= secondPart.length; i++) {
        const extendedInitials = firstPart.charAt(0).toUpperCase() + 
          secondPart.charAt(0).toUpperCase() + 
          secondPart.substring(1, i).toLowerCase();
        candidateName = `${firstName} ${extendedInitials}`;
        
        if (!existingNames.has(candidateName.toLowerCase())) {
          return candidateName;
        }
      }
      
      // If all are taken, append a number
      const fullInitials = firstPart.charAt(0).toUpperCase() + 
        secondPart.charAt(0).toUpperCase() + 
        secondPart.substring(1).toLowerCase();
      let counter = 2;
      while (true) {
        candidateName = `${firstName} ${fullInitials}${counter}`;
        if (!existingNames.has(candidateName.toLowerCase())) {
          return candidateName;
        }
        counter++;
        if (counter > 100) {
          return `${firstName} ${fullInitials}${Date.now()}`;
        }
      }
    }
  }
  
  // For non-hyphenated names like "Smith"
  // Try progressively longer initials (first letter uppercase, rest lowercase)
  for (let i = 1; i <= lastName.length; i++) {
    const initials = lastName.charAt(0).toUpperCase() + lastName.substring(1, i).toLowerCase();
    const candidateName = `${firstName} ${initials}`;
    
    if (!existingNames.has(candidateName.toLowerCase())) {
      return candidateName;
    }
  }

  // If all initials are taken, append a number
  let counter = 2;
  const fullInitials = lastName.charAt(0).toUpperCase() + lastName.substring(1).toLowerCase();
  while (true) {
    const candidateName = `${firstName} ${fullInitials}${counter}`;
    if (!existingNames.has(candidateName.toLowerCase())) {
      return candidateName;
    }
    counter++;
    // Safety limit
    if (counter > 100) {
      return `${firstName} ${fullInitials}${Date.now()}`;
    }
  }
};

/**
 * UserService
 * Handles operations for user documents
 */
class UserService {
  /**
   * Find or create a user by email
   * @param {string} email - User email address
   * @param {string} name - User name
   * @returns {Promise<{userId: string, isNew: boolean}>} User document ID and whether it's new
   */
  async findOrCreateUser(email, name) {
    try {
      // Normalize email to lowercase
      const normalizedEmail = email.toLowerCase().trim();

      // Check if user exists by email
      const usersRef = collection(db, USERS_COLLECTION);
      const q = query(usersRef, where('email', '==', normalizedEmail));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        // User exists, return existing user
        const docSnapshot = querySnapshot.docs[0];
        return {
          userId: docSnapshot.id,
          isNew: false
        };
      }

      // Create new user
      const trimmedName = name.trim();
      const displayName = await this.getUniqueDisplayName(trimmedName);

      const userData = camelToSnake({
        email: normalizedEmail,
        name: trimmedName,
        displayName: displayName,
        dateCreated: Date.now(),
        dateUpdated: Date.now()
      });

      const newUserRef = await addDoc(usersRef, userData);
      const userId = newUserRef.id;

      // Add the document ID (userId) to the document itself
      const userDocRef = doc(db, USERS_COLLECTION, userId);
      await setDoc(userDocRef, camelToSnake({ userId }), { merge: true });

      return {
        userId: userId,
        isNew: true
      };
    } catch (error) {
      console.error('Error finding or creating user:', error);
      throw error;
    }
  }

  /**
   * Get user by ID
   * @param {string} userId - User document ID
   * @returns {Promise<Object | null>} User data or null if not found
   */
  async getUserById(userId) {
    try {
      const userRef = doc(db, USERS_COLLECTION, userId);
      const docSnapshot = await getDoc(userRef);

      if (!docSnapshot.exists()) {
        return null;
      }

      return transformDocument(docSnapshot.id, docSnapshot.data());
    } catch (error) {
      console.error('Error fetching user:', error);
      throw error;
    }
  }

  /**
   * Get user by email
   * @param {string} email - User email address
   * @returns {Promise<Object | null>} User data or null if not found
   */
  async getUserByEmail(email) {
    try {
      const normalizedEmail = email.toLowerCase().trim();
      const usersRef = collection(db, USERS_COLLECTION);
      const q = query(usersRef, where('email', '==', normalizedEmail));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return null;
      }

      const docSnapshot = querySnapshot.docs[0];
      return transformDocument(docSnapshot.id, docSnapshot.data());
    } catch (error) {
      console.error('Error fetching user by email:', error);
      throw error;
    }
  }

  /**
   * Update user information
   * @param {string} userId - User document ID
   * @param {Object} updates - Partial update object with camelCase keys
   * @returns {Promise<void>}
   */
  async updateUser(userId, updates) {
    try {
      const userRef = doc(db, USERS_COLLECTION, userId);

      // If name is being updated, regenerate display_name with uniqueness check
      const processedUpdates = { ...updates };
      if (processedUpdates.name) {
        processedUpdates.displayName = await this.getUniqueDisplayName(processedUpdates.name);
      }

      const firestoreUpdates = camelToSnake({
        ...processedUpdates,
        dateUpdated: Date.now()
      });
      await setDoc(userRef, firestoreUpdates, { merge: true });
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  /**
   * Generate display name from full name
   * Exported as a utility method for use outside the service
   * @param {string} name - Full name
   * @returns {string} Display name
   */
  generateDisplayName(name) {
    return generateDisplayName(name);
  }

  /**
   * Fetch all existing display names from Firestore
   * @returns {Promise<Set<string>>} Set of display names (lowercase)
   */
  async fetchAllDisplayNames() {
    try {
      const usersRef = collection(db, USERS_COLLECTION);
      const querySnapshot = await getDocs(usersRef);
      
      const displayNames = new Set();
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const displayName = data.display_name || data.displayName;
        if (displayName) {
          displayNames.add(displayName.toLowerCase());
        }
      });
      
      return displayNames;
    } catch (error) {
      console.error('Error fetching display names:', error);
      throw error;
    }
  }

  /**
   * Check if a display name is already taken
   * @param {string} displayName - Display name to check
   * @returns {Promise<boolean>} True if taken, false if available
   */
  async isDisplayNameTaken(displayName) {
    try {
      const usersRef = collection(db, USERS_COLLECTION);
      const q = query(usersRef, where('display_name', '==', displayName));
      const querySnapshot = await getDocs(q);
      
      return !querySnapshot.empty;
    } catch (error) {
      console.error('Error checking display name:', error);
      throw error;
    }
  }

  /**
   * Get a unique display name for a user
   * If the base display name is taken, progressively add more initials from last name
   * @param {string} fullName - User's full name (e.g., "John Smith" or "John Tom-Smith")
   * @returns {Promise<string>} Unique display name
   */
  async getUniqueDisplayName(fullName) {
    try {
      if (!fullName || typeof fullName !== 'string') {
        return '';
      }

      const trimmedName = fullName.trim();
      if (!trimmedName) {
        return '';
      }

      const nameParts = trimmedName.split(/\s+/).filter(part => part.length > 0);
      if (nameParts.length === 0) {
        return '';
      }

      const firstName = nameParts[0];

      // If only one name part, just return first name (can't add initials)
      if (nameParts.length === 1) {
        const existingNames = await this.fetchAllDisplayNames();
        if (!existingNames.has(firstName.toLowerCase())) {
          return firstName;
        }
        // Append number if first name alone is taken
        let counter = 2;
        while (existingNames.has(`${firstName}${counter}`.toLowerCase())) {
          counter++;
          if (counter > 100) {
            return `${firstName}${Date.now()}`;
          }
        }
        return `${firstName}${counter}`;
      }

      const lastName = nameParts[nameParts.length - 1];

      // Fetch all existing display names
      const existingNames = await this.fetchAllDisplayNames();

      // Generate unique display name with progressive initials
      return generateUniqueDisplayName(firstName, lastName, existingNames);
    } catch (error) {
      console.error('Error generating unique display name:', error);
      // Fallback to basic display name
      return generateDisplayName(fullName);
    }
  }
}

// Export singleton instance
export default new UserService();
